import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import * as LDClient from "launchdarkly-js-client-sdk";
import { RootState } from "types/Store";
import usableActions from "utils/store/usableActions";

type _KnownLDFlags = {
  quotesDailySummaryEmails: boolean;
  demoOnly: boolean;
  useSendGrid: boolean;
  /** @deprecated This is enabled at 100% and should not be turned off */
  shipmentsLTL: boolean;
  /** @deprecated This is enabled at 100% and should not be turned off */
  shipmentsTab: boolean;
  bulkImport: boolean;
  bidsRFP: boolean;
  doNotSendBookingEmails: boolean;
  internalOnly: boolean;
  /** @deprecated This is enabled at 100% and should not be turned off */
  enableChat: boolean;
  enableRFPInvitePartners: boolean;
  enableRFPCloseAction: boolean;
  enableRFPPartnersTab: boolean;
  bulkFinalizeAwards: boolean;
  enableSockets: boolean;
  allowedFileExtensions: { allowedExtensions: Array<string> };
  /** @deprecated This is enabled at 100% and should not be turned off */
  newShipmentFilters: boolean;
  dispatchRequests: boolean;
  locationBookingNotes: boolean;
  editTrucksV2: boolean;
  multiLoadQuotes: boolean;
  templatesForExistingQRs: boolean;
  brokerBidUploads: boolean;
  /** @deprecated use `featuredBrokers2` */
  featuredBrokers: boolean;
  brokerRateEmails: { brokerEmails: string[]; shipperIds: string[] };
  /** @summary For each broker email in the map, is an array of shipper IDs they are featured for. Record<brokerEmail, shipperId[]> */
  featuredBrokers2: Record<string, number[]>;
  brokerReferral: { showForm: boolean; offerAmount: number; numberOfContacts: number };
  rolloutAddChatParticipants: boolean;
  configureIntermodal: { shipperIds: number[] };
  rolloutBulkEditFuel: boolean;
  rolloutLoadStatuses: boolean;
  enableShipperLoadsIndexPage: boolean;
  enableShipmentReadOnlyLinkSharing: boolean;
  enableAddLanesToLiveRfp: boolean;
  enableUserClaimFlow: boolean;
  rolloutBrokerScorecards: boolean;
  configureBrokerScorecards: {
    scoringForCompleted: number;
    scoringForCompletedLate: number;
  };
  enableLaneBenchmark: boolean;
  enableBOL: boolean;
  enableShareDocuments: boolean; // share via chat
  enableShareDocumentsViaEmail: boolean; // share via email
  enableDispatchAutoConfirm: boolean;
  enableDrayage: boolean;
  enableImportAddresses: boolean;
  enableBrokerAppBar: boolean;
  enableBrokerQuotesPage: boolean;
  /** @deprecated This is enabled at 100% and should not be turned off */
  enableInternationalQuotesChat: boolean;
  enableQuoteConversationsTab: boolean;
  enableFeaturedBrokerDashboard: boolean;
  enableBrokerBenchmark: boolean;
  enableLtlPartialMode: boolean;
  enableRfpBulkSpotQuote: boolean;
  /** @description This is a release control flag to release chat as a feature to brokers @see https://app.launchdarkly.com/projects/default/flags/releaseBrokerChat/targeting?env=dev&env=staging&env=production&selected-env=dev  */
  releaseBrokerChat: boolean;
  enableBenchmarkRateEstimates: boolean;
  releaseQuoteRequestFiles: boolean;
  enableLtlAutoPricing: boolean;
  configureApiAutoPricingToggleDefault: boolean;
  enableBrokerReports: boolean;
  enableFreightClassCalculator: boolean;
  requestFlowV2: boolean;
  defaultHideInternalRefTillDispatch?: boolean;
  ftlQuoteDetailsPageV2?: boolean;
  ftlCargoGroups?: boolean;
};

export type KnownLDFlags = Partial<_KnownLDFlags>;

export type KnownLDFlagKeys = keyof KnownLDFlags;

interface LDFeatureFlagsSliceState {
  flags: KnownLDFlags;
}

const initialState: LDFeatureFlagsSliceState = {
  flags: {},
};

const ldFeatureFlagsSliceName = "ldFeatureFlagsSlice";

const ldFeatureFlagsSlice = createSlice({
  name: ldFeatureFlagsSliceName,
  initialState,
  reducers: {
    addFlagsChangeSet: (state, action: PayloadAction<LDClient.LDFlagChangeset>) => {
      const newFlags = action.payload;
      Object.entries(newFlags).forEach(([key, { current }]) => {
        state.flags[key as KnownLDFlagKeys] = current;
      });
    },
    addFlagsSet: (state, action: PayloadAction<LDClient.LDFlagSet>) => {
      const flags = action.payload;
      state.flags = { ...state.flags, ...flags };
    },
  },
});

export const { useAddFlagsChangeSet, useAddFlagsSet } = usableActions(ldFeatureFlagsSlice.actions);
export default ldFeatureFlagsSlice;

export const selectAllLDFlags = (state: RootState): KnownLDFlags => state.ldFeatureFlagsSlice.flags;

export const selectLDFlag = createSelector(
  [selectAllLDFlags, (_state: RootState, key: KnownLDFlagKeys): KnownLDFlagKeys => key],
  (flags, key) => flags[key]
);
